/**
 * DisclosureTemplateLifecyclePage — Documents & Disclosures · Template Lifecycle.
 *
 * Per-template-type authoring workflow stages for the Disclosure Designer
 * (4-step bodies / 3-step partials per Bill 2026-05-12 Round 2). Each row maps
 * a template type to its ordered stage list, an optional stage→role map, and
 * whether peer review is required before publish.
 *
 * Contract:
 *   - Read:  GET    /v3/admin/disclosure-template-lifecycle?includeInactive=true
 *   - Write: POST   /v3/admin/disclosure-template-lifecycle           (upsert)
 *            DELETE /v3/admin/disclosure-template-lifecycle/{type}    (soft-delete)
 *
 * Migrated 2026-06-16 from ROP-teams Admin (operational app writes no config).
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL → buildAdminAuthHeaders omits the bypass-key
 * (MSAL bearer carries authority; no secret in the bundle).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  buildAdminAuthHeaders,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface Row {
  templateType: string;
  stagesJson: string;
  requiresPeerReview: boolean;
  stageRoleMapJson: string | null;
  isActive: boolean;
}

interface EditDraft extends Row {
  isNew: boolean;
}

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    templateType: '',
    stagesJson: '["draft","approval","publish"]',
    requiresPeerReview: false,
    stageRoleMapJson: '',
    isActive: true,
  };
}

export function DisclosureTemplateLifecyclePage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const r = await client.http.get<{ success: boolean; data?: ReadonlyArray<Row>; error?: string }>(
          '/v3/admin/disclosure-template-lifecycle?includeInactive=true',
        );
        if (cancelled) return;
        if (r.success && Array.isArray(r.data)) setRows(r.data);
        else setLoadError(r.error ?? 'Failed to load lifecycles.');
      } catch (e) {
        if (cancelled) return;
        setLoadError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.templateType.trim()) {
      setSaveError('Template type is required.');
      return;
    }
    try {
      JSON.parse(draft.stagesJson);
    } catch {
      setSaveError('Stages must be a valid JSON array.');
      return;
    }
    if (draft.stageRoleMapJson?.trim()) {
      try {
        JSON.parse(draft.stageRoleMapJson);
      } catch {
        setSaveError('Stage role map must be valid JSON.');
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    try {
      const body = { ...draft, stageRoleMapJson: draft.stageRoleMapJson?.trim() || null };
      const r = await client.http.post<{ success: boolean; error?: string }>(
        '/v3/admin/disclosure-template-lifecycle',
        { headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL), body },
      );
      if (!r.success) throw new Error(r.error ?? 'save failed');
      const tt = draft.templateType.trim();
      setDraft(null);
      setToast({ tone: 'info', text: draft.isNew ? `Created lifecycle "${tt}".` : `Updated "${tt}".` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleRetire = useCallback(async () => {
    if (!draft || draft.isNew) return;
    const tt = draft.templateType.trim();
    setSaving(true);
    setSaveError(null);
    try {
      const r = await client.http.del<{ success: boolean; error?: string }>(
        `/v3/admin/disclosure-template-lifecycle/${encodeURIComponent(tt)}`,
        { headers: buildAdminAuthHeaders(undefined, ADMIN_AUTH_TOKEN_SENTINEL) },
      );
      if (!r.success) throw new Error(r.error ?? 'delete failed');
      setDraft(null);
      setToast({ tone: 'warn', text: `Retired lifecycle "${tt}".` });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<Row>> = [
    { header: 'Template type', cell: (r) => <code className="cell-mono--bold">{r.templateType}</code> },
    { header: 'Stages', cell: (r) => <code className="cell-mono">{r.stagesJson}</code> },
    {
      header: 'Peer review',
      cell: (r) => (r.requiresPeerReview ? <Pill tone="warn">Required</Pill> : <Pill tone="muted">No</Pill>),
    },
    {
      header: 'Status',
      cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">retired</Pill>),
    },
    {
      header: '',
      cell: (r) => (
        <Button onClick={() => { setDraft({ ...r, stageRoleMapJson: r.stageRoleMapJson ?? '', isNew: false }); setSaveError(null); }}>
          Edit
        </Button>
      ),
    },
  ];

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load lifecycles: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading template lifecycles…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={`Template Lifecycles — ${rows.length}`}
        actions={
          <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
            + New lifecycle
          </Button>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={rows}
          rowKey={(r) => r.templateType}
          emptyState={<p className="empty">No template lifecycles defined yet.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'New lifecycle' : `Edit ${draft?.templateType ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={640}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && draft.isActive && (
                <Button onClick={() => void handleRetire()} disabled={saving}>
                  Retire
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Template type (key)"
              value={draft.templateType}
              onChange={(v) => setDraft({ ...draft, templateType: v })}
              placeholder="body / signature-partial / merge-field-set"
              disabled={!draft.isNew}
              monospace
              hint={draft.isNew ? 'Permanent key — retire and recreate to re-key.' : 'Type is fixed once created.'}
            />

            <TextArea
              label="Stages (JSON array)"
              value={draft.stagesJson}
              onChange={(v) => setDraft({ ...draft, stagesJson: v })}
              rows={3}
              placeholder='["draft","internal-review","approval","publish"]'
            />

            <TextArea
              label="Stage role map (JSON object, optional)"
              value={draft.stageRoleMapJson ?? ''}
              onChange={(v) => setDraft({ ...draft, stageRoleMapJson: v })}
              rows={3}
              placeholder='{"draft":"DisclosureManager","approval":"DisclosureManager"}'
            />

            <BoolField
              label="Requires peer review"
              value={draft.requiresPeerReview}
              onChange={(v) => setDraft({ ...draft, requiresPeerReview: v })}
              hint="Typically true for full bodies. Adds a second-reviewer gate before publish."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Retired lifecycles are kept for audit but no longer offered in the designer."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}
